import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { QuoteProvider } from "@/components/QuoteProvider";
import WhatsAppButton from "@/components/WhatsAppButton";
import ScrollProgress from "@/components/ScrollProgress";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import MarketSegments from "@/pages/MarketSegments";
import Infrastructure from "@/pages/Infrastructure";
import PCBManufacturing from "@/pages/PCBManufacturing";
import Gallery from "@/pages/Gallery";
import Quality from "@/pages/Quality";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-white p-8">
                    <div className="max-w-md text-center">
                        <h1 className="text-2xl font-bold text-navy">Something went wrong</h1>
                        <p className="mt-3 text-sm text-slate-500">Please refresh the page to continue.</p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const PAGE_META = {
    "/": {
        title: "PCB Manufacturer in Sahibabad, Ghaziabad | Unimicron Technology India",
        description: "Unimicron Technology India — professional PCB manufacturing in Sahibabad, Ghaziabad, UP. Single layer, double layer, multilayer and metal core PCBs for electronics, industrial and power applications. Get a quote today.",
    },
    "/about": {
        title: "About Us | Unimicron Technology India — PCB Manufacturer",
        description: "Unimicron Technology India provides PCB manufacturing solutions for electronics manufacturers and industrial customers with a focus on quality, competitive pricing, reliable service and timely delivery.",
    },
    "/market-segments": {
        title: "Market Segments | Unimicron Technology India",
        description: "PCBs manufactured for power electronics, UPS and inverters, energy meters, IoT devices, industrial automation, LED lighting, automotive, networking, telecom and consumer electronics.",
    },
    "/infrastructure": {
        title: "Infrastructure | Unimicron Technology India",
        description: "PCB manufacturing capabilities, production process, quality inspection, testing and packaging at Unimicron Technology India, Sahibabad, Ghaziabad.",
    },
    "/pcb-manufacturing": {
        title: "PCB Manufacturing — Single, Double, Multilayer & Metal Core | Unimicron Technology India",
        description: "Detailed PCB manufacturing sections: single layer, double layer, multilayer and metal core PCBs manufactured to customer design data and specification.",
    },
    "/gallery": {
        title: "PCB Photo Gallery | Unimicron Technology India",
        description: "Photo gallery of PCBs and production facility at Unimicron Technology India — single layer, double layer, multilayer and metal core PCBs.",
    },
    "/quality": {
        title: "Quality | Unimicron Technology India",
        description: "Consistent PCB manufacturing, multi-stage inspection, customer specifications and reliable PCB quality at Unimicron Technology India.",
    },
    "/contact": {
        title: "Contact Us | Unimicron Technology India — PCB Manufacturer, Ghaziabad",
        description: "Contact Unimicron Technology India, H-60, Durga Industrial Area, Sahibabad, Ghaziabad, Uttar Pradesh. Phone +91 9621235273, email sales@unimicronind.com.",
    },
    "/admin": { title: "Admin | Unimicron Technology India", description: "" },
};

function SeoManager() {
    const { pathname } = useLocation();
    useEffect(() => {
        const meta = PAGE_META[pathname] || PAGE_META["/"];
        if (!meta) return;
        document.title = meta.title;
        const desc = document.querySelector('meta[name="description"]');
        if (desc && meta.description) desc.setAttribute("content", meta.description);
        let robots = document.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement("meta");
            robots.setAttribute("name", "robots");
            document.head.appendChild(robots);
        }
        robots.setAttribute("content", pathname === "/admin" ? "noindex, nofollow" : "index, follow");
    }, [pathname]);
    return null;
}

function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => {
                const el = document.getElementById(hash.slice(1));
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                } else {
                    window.scrollTo(0, 0);
                }
            }, 450);
            return () => clearTimeout(t);
        }
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

function SmoothScroll() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
        let rafId = requestAnimationFrame(function loop(t) {
            lenis.raf(t);
            rafId = requestAnimationFrame(loop);
        });
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);
    return null;
}

function AnimatedRoutes() {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
            >
                <Routes location={location}>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/market-segments" element={<MarketSegments />} />
                    <Route path="/infrastructure" element={<Infrastructure />} />
                    <Route path="/pcb-manufacturing" element={<PCBManufacturing />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/quality" element={<Quality />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </motion.div>
        </AnimatePresence>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <QuoteProvider>
                    <SmoothScroll />
                    <ScrollManager />
                    <SeoManager />
                    <ScrollProgress />
                    <Header />
                    <main>
                        <AnimatedRoutes />
                    </main>
                    <Footer />
                    <WhatsAppButton />
                    <Toaster position="top-right" richColors />
                </QuoteProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
